import { httpClient } from './http-client';
import type { ApiResponse } from '../types/api';

export interface Budget {
  id: string;
  name: string;
  period_type: 'monthly' | 'yearly' | 'custom';
  start_date: string;
  end_date: string;
  is_active: boolean;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

interface BudgetItem {
  id: string;
  budget_id: string;
  category_id?: string | null;
  category_group_id?: string | null;
  name: string;
  budgeted_amount: string;
  rollover: boolean;
  notes?: string | null;
  category_name?: string | null;
  category_group_name?: string | null;
  category_group_type?: string | null;
  category_color?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BudgetVsActualItem {
  budget_item_id: string;
  name: string;
  category_id?: string | null;
  category_group_id?: string | null;
  category_name?: string | null;
  category_group_name?: string | null;
  category_color?: string | null;
  budgeted_amount: string;
  actual_amount: string;
  remaining_amount: string;
  percent_used: string;
}

export interface BudgetVsActualResponse {
  items: BudgetVsActualItem[];
  total_budgeted: string;
  total_actual: string;
  total_remaining: string;
  total_percent: string;
}

export interface CreateBudgetRequest {
  name: string;
  period_type: string;
  start_date: string;
  end_date: string;
  notes?: string;
}

export interface CreateBudgetItemRequest {
  category_id?: string;
  category_group_id?: string;
  name: string;
  budgeted_amount: string;
  rollover?: boolean;
  notes?: string;
}

export class BudgetsService {
  private static readonly BASE_URL = '/budgets';

  static async listBudgets(activeOnly?: boolean): Promise<ApiResponse<{ budgets: Budget[]; total: number }>> {
    const url = activeOnly
      ? `${BudgetsService.BASE_URL}?active_only=true`
      : BudgetsService.BASE_URL;
    const raw = await httpClient.get<any>(url);
    const data = (raw as any).data || raw;
    return { data: { budgets: data.budgets || [], total: data.total || 0 } };
  }

  static async getBudget(id: string): Promise<ApiResponse<Budget>> {
    const raw = await httpClient.get<any>(`${BudgetsService.BASE_URL}/${id}`);
    return { data: (raw as any).data || raw };
  }

  static async createBudget(req: CreateBudgetRequest): Promise<ApiResponse<Budget>> {
    const raw = await httpClient.post<any>(BudgetsService.BASE_URL, req);
    return { data: (raw as any).data || raw };
  }

  static async updateBudget(id: string, req: Partial<CreateBudgetRequest> & { is_active?: boolean }): Promise<ApiResponse<Budget>> {
    const raw = await httpClient.put<any>(`${BudgetsService.BASE_URL}/${id}`, req);
    return { data: (raw as any).data || raw };
  }

  static async deleteBudget(id: string): Promise<void> {
    await httpClient.delete(`${BudgetsService.BASE_URL}/${id}`);
  }

  static async listBudgetItems(budgetId: string): Promise<ApiResponse<{ items: BudgetItem[]; total: number }>> {
    const raw = await httpClient.get<any>(`${BudgetsService.BASE_URL}/${budgetId}/items`);
    const data = (raw as any).data || raw;
    return { data: { items: data.items || [], total: data.total || 0 } };
  }

  static async createBudgetItem(budgetId: string, req: CreateBudgetItemRequest): Promise<ApiResponse<BudgetItem>> {
    const raw = await httpClient.post<any>(`${BudgetsService.BASE_URL}/${budgetId}/items`, req);
    return { data: (raw as any).data || raw };
  }

  static async updateBudgetItem(budgetId: string, itemId: string, req: Partial<CreateBudgetItemRequest>): Promise<ApiResponse<BudgetItem>> {
    const raw = await httpClient.put<any>(`${BudgetsService.BASE_URL}/${budgetId}/items/${itemId}`, req);
    return { data: (raw as any).data || raw };
  }

  static async deleteBudgetItem(budgetId: string, itemId: string): Promise<void> {
    await httpClient.delete(`${BudgetsService.BASE_URL}/${budgetId}/items/${itemId}`);
  }

  static async getBudgetVsActual(budgetId: string): Promise<ApiResponse<BudgetVsActualResponse>> {
    const raw = await httpClient.get<any>(`${BudgetsService.BASE_URL}/${budgetId}/vs-actual`);
    const data = (raw as any).data || raw;
    return { data };
  }
}
